import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Create axios instance with auth header
const createAuthAxios = () => {
  let token = null;
  try { token = localStorage.getItem('ff_token'); } catch {}
  return axios.create({
    baseURL: API,
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });
};

// Forms API
export const formsApi = {
  getAll: async (params = {}) => {
    const api = createAuthAxios();
    const response = await api.get('/forms/', { params });
    return response.data;
  },
  
  get: async (id) => {
    const api = createAuthAxios();
    const response = await api.get(`/forms/${id}`);
    return response.data;
  },
  
  getPublic: async (id) => {
    const response = await axios.get(`${API}/forms/public/${id}`);
    return response.data;
  },
  
  create: async (data) => {
    const api = createAuthAxios();
    const response = await api.post('/forms/', data);
    return response.data;
  },
  
  update: async (id, data) => {
    const api = createAuthAxios();
    const response = await api.put(`/forms/${id}`, data);
    return response.data;
  },
  
  delete: async (id) => {
    const api = createAuthAxios();
    const response = await api.delete(`/forms/${id}`);
    return response.data;
  },
  
  duplicate: async (id, title) => {
    const api = createAuthAxios();
    const body = title ? { title } : {};
    const response = await api.post(`/forms/${id}/duplicate`, body);
    return response.data;
  }
};

// Submissions API
export const submissionsApi = {
  getForForm: async (formId, params = {}) => {
    const api = createAuthAxios();
    const response = await api.get(`/forms/${formId}/submissions`, { params });
    return response.data;
  },
  
  get: async (formId, submissionId) => {
    const api = createAuthAxios();
    const response = await api.get(`/forms/${formId}/submissions/${submissionId}`);
    return response.data;
  },
  
  create: async (data) => {
    // Backend expects /forms/{form_id}/submissions
    const formId = data.form_id;
    const response = await axios.post(`${API}/forms/${formId}/submissions`, data);
    return response.data;
  },
  
  delete: async (formId, submissionId) => {
    const api = createAuthAxios();
    const response = await api.delete(`/forms/${formId}/submissions/${submissionId}`);
    return response.data;
  },
  
  deleteAll: async (formId) => {
    const api = createAuthAxios();
    const response = await api.delete(`/forms/${formId}/submissions`);
    return response.data;
  },
  
  export: async (formId, format = 'json', { ids } = {}) => {
    const api = createAuthAxios();
    // Binary formats (Parquet, .numbers iWork bundle) must be fetched
    // as ArrayBuffer, not decoded as text/JSON, or the file will be
    // corrupted on the way through the browser.
    const isBinary = format === 'parquet' || format === 'numbers';
    const responseType = isBinary ? 'arraybuffer' : (format === 'json' ? 'json' : 'text');
    const params = {};
    if (ids && ids.length) params.ids = ids.join(',');
    try {
      const response = await api.get(`/forms/${formId}/submissions/export/${format}`, { responseType, params });
      if (format === 'csv') return { csv: response.data };
      if (format === 'json') return { submissions: response.data };
      if (format === 'parquet') return { buffer: response.data, contentType: 'application/vnd.apache.parquet' };
      if (format === 'numbers') return { buffer: response.data, contentType: 'application/vnd.apple.numbers' };
      return { text: response.data, contentType: response.headers?.['content-type'] || 'text/plain' };
    } catch (err) {
      // When axios is set to `arraybuffer`, error bodies also arrive
      // as an ArrayBuffer. Decode them so callers can surface the
      // real server message instead of a generic axios stringification.
      const data = err?.response?.data;
      if (data instanceof ArrayBuffer) {
        try {
          const text = new TextDecoder().decode(data);
          const parsed = JSON.parse(text);
          err.serverDetail = parsed.detail || text;
        } catch {
          err.serverDetail = new TextDecoder().decode(data);
        }
      } else if (data && typeof data === 'object' && data.detail) {
        err.serverDetail = data.detail;
      } else if (typeof data === 'string') {
        err.serverDetail = data;
      }
      throw err;
    }
  },
  
  analytics: async (formId) => {
    const api = createAuthAxios();
    const response = await api.get(`/forms/${formId}/analytics`);
    return response.data;
  },

  updateTags: async (formId, submissionId, tags) => {
    const api = createAuthAxios();
    const response = await api.patch(
      `/forms/${formId}/submissions/${submissionId}/tags`,
      { tags }
    );
    return response.data;
  }
};

// Abandoned submissions tracking (PRO follow-up emails)
export const abandonedApi = {
  // Public, fire-and-forget. Resolves silently; never throws to UI.
  track: async ({ form_id, email, partial_data, field_count }) => {
    try {
      await axios.post(`${API}/abandoned-submissions/track`, {
        form_id,
        email,
        partial_data: partial_data || {},
        field_count: field_count || 0,
      });
    } catch (e) {
      // Intentionally silent — abandonment tracking must never block UX.
    }
  }
};

// Templates API
export const templatesApi = {
  getAll: async (params = {}) => {
    const response = await axios.get(`${API}/templates/`, { params });
    return response.data;
  },
  
  get: async (id) => {
    const response = await axios.get(`${API}/templates/${id}`);
    return response.data;
  },
  
  getCategories: async () => {
    const response = await axios.get(`${API}/templates/categories`);
    return response.data;
  },
  
  use: async (id) => {
    const api = createAuthAxios();
    const response = await api.post(`/templates/${id}/use`);
    return response.data;
  },

  mine: async () => {
    const api = createAuthAxios();
    const response = await api.get('/templates/mine');
    return response.data;
  },

  saveFromForm: async (formId, payload) => {
    const api = createAuthAxios();
    const response = await api.post(`/templates/from-form/${formId}`, payload);
    return response.data;
  },

  delete: async (id) => {
    const api = createAuthAxios();
    const response = await api.delete(`/templates/${id}`);
    return response.data;
  }
};

// Experiments (A/B testing) API
export const experimentsApi = {
  start: async (formId) => {
    const api = createAuthAxios();
    const response = await api.post(`/experiments/forms/${formId}`);
    return response.data;
  },
  get: async (formId) => {
    const api = createAuthAxios();
    const response = await api.get(`/experiments/forms/${formId}`);
    return response.data;
  },
  promote: async (formId, variantLabel) => {
    const api = createAuthAxios();
    const response = await api.post(`/experiments/forms/${formId}/promote/${variantLabel}`);
    return response.data;
  },
  end: async (formId) => {
    const api = createAuthAxios();
    const response = await api.post(`/experiments/forms/${formId}/end`);
    return response.data;
  },
  delete: async (formId) => {
    const api = createAuthAxios();
    const response = await api.delete(`/experiments/forms/${formId}`);
    return response.data;
  },
};

// Users API
export const usersApi = {
  update: async (data) => {
    const api = createAuthAxios();
    const response = await api.put('/users/me', data);
    return response.data;
  },
  
  changePassword: async (currentPassword, newPassword) => {
    const api = createAuthAxios();
    const response = await api.post('/users/me/change-password', null, {
      params: { current_password: currentPassword, new_password: newPassword }
    });
    return response.data;
  },
  
  deleteAccount: async (password) => {
    const api = createAuthAxios();
    const response = await api.delete('/users/me', { params: { password } });
    return response.data;
  },
  
  setupMfa: async () => {
    const api = createAuthAxios();
    const response = await api.post('/security/2fa/setup');
    return response.data;
  },
  
  verifyMfa: async (code) => {
    const api = createAuthAxios();
    const response = await api.post('/security/2fa/verify', null, { params: { code } });
    return response.data;
  },
  
  disableMfa: async (code) => {
    const api = createAuthAxios();
    const response = await api.post('/security/2fa/disable', null, { params: { code } });
    return response.data;
  },

  getMfaStatus: async () => {
    const api = createAuthAxios();
    const response = await api.get('/security/2fa/status');
    return response.data;
  },
  
  getBilling: async () => {
    const api = createAuthAxios();
    const response = await api.get('/users/me/billing');
    return response.data;
  },
  
  upgrade: async () => {
    const api = createAuthAxios();
    const response = await api.post('/users/me/upgrade');
    return response.data;
  },
  
  downgrade: async () => {
    const api = createAuthAxios();
    const response = await api.post('/users/me/downgrade');
    return response.data;
  },
  
  createCustomerPortal: async () => {
    const api = createAuthAxios();
    const response = await api.post('/billing/customer-portal');
    return response.data;
  }
};

export const apiKeysApi = {
  list: async () => {
    const api = createAuthAxios();
    const response = await api.get('/api-keys');
    return response.data;
  },
  create: async (name) => {
    const api = createAuthAxios();
    const response = await api.post('/api-keys', { name });
    return response.data;
  },
  revoke: async (keyId) => {
    const api = createAuthAxios();
    const response = await api.delete(`/api-keys/${keyId}`);
    return response.data;
  }
};

// Admin API
export const adminApi = {
  getUsers: async (params = {}) => {
    const api = createAuthAxios();
    const response = await api.get('/admin/users', { params });
    return response.data;
  },
  
  getUser: async (id) => {
    const api = createAuthAxios();
    const response = await api.get(`/admin/users/${id}`);
    return response.data;
  },
  
  banUser: async (id) => {
    const api = createAuthAxios();
    const response = await api.post(`/admin/users/${id}/ban`);
    return response.data;
  },
  
  unbanUser: async (id) => {
    const api = createAuthAxios();
    const response = await api.post(`/admin/users/${id}/unban`);
    return response.data;
  },
  
  deleteUser: async (id) => {
    const api = createAuthAxios();
    const response = await api.delete(`/admin/users/${id}`);
    return response.data;
  },
  
  clearSubmissions: async (id) => {
    const api = createAuthAxios();
    const response = await api.delete(`/admin/users/${id}/submissions`);
    return response.data;
  },
  
  getStats: async () => {
    const api = createAuthAxios();
    const response = await api.get('/admin/stats');
    return response.data;
  },

  getTrafficRevenue: async (days = 30) => {
    const api = createAuthAxios();
    const response = await api.get('/admin/analytics/traffic-revenue', { params: { days } });
    return response.data;
  },
  
  // Platform Settings
  getSettings: async () => {
    const api = createAuthAxios();
    const response = await api.get('/settings/platform');
    return response.data;
  },
  
  updateSettings: async (data) => {
    const api = createAuthAxios();
    const response = await api.post('/settings/platform', data);
    return response.data;
  },
  
  // Integration Credentials
  updateIntegrations: async (credentials) => {
    const api = createAuthAxios();
    const response = await api.post('/settings/credentials', credentials);
    return response.data;
  },
  
  getIntegrationStatus: async () => {
    const api = createAuthAxios();
    const response = await api.get('/settings/integrations/status');
    return response.data;
  },
  
  getCredentials: async () => {
    const api = createAuthAxios();
    const response = await api.get('/settings/credentials');
    return response.data;
  },

  // Blog management (admin only)
  listBlogPosts: async (params = {}) => {
    const api = createAuthAxios();
    const response = await api.get('/admin/blog', { params });
    return response.data;
  },

  getBlogPost: async (id) => {
    const api = createAuthAxios();
    const response = await api.get(`/admin/blog/${id}`);
    return response.data;
  },

  createBlogPost: async (payload) => {
    const api = createAuthAxios();
    const response = await api.post('/admin/blog', payload);
    return response.data;
  },

  updateBlogPost: async (id, payload) => {
    const api = createAuthAxios();
    const response = await api.put(`/admin/blog/${id}`, payload);
    return response.data;
  },

  deleteBlogPost: async (id) => {
    const api = createAuthAxios();
    const response = await api.delete(`/admin/blog/${id}`);
    return response.data;
  },
};

// Help API
export const helpApi = {
  getArticles: async (params = {}) => {
    const response = await axios.get(`${API}/help/articles`, { params });
    return response.data;
  },
  
  getArticle: async (id) => {
    const response = await axios.get(`${API}/help/articles/${id}`);
    return response.data;
  },
  
  getCategories: async () => {
    const response = await axios.get(`${API}/help/articles/categories`);
    return response.data;
  },
  
  submitFeatureRequest: async (title, description) => {
    const api = createAuthAxios();
    const response = await api.post('/help/feature-requests', null, {
      params: { title, description }
    });
    return response.data;
  },
  
  getFeatureRequests: async (params = {}) => {
    const response = await axios.get(`${API}/help/feature-requests`, { params });
    return response.data;
  },
  
  voteFeatureRequest: async (id) => {
    const api = createAuthAxios();
    const response = await api.post(`/help/feature-requests/${id}/vote`);
    return response.data;
  },
  
  submitBugReport: async (title, description, severity = 'medium') => {
    const api = createAuthAxios();
    const response = await api.post('/help/bug-reports', null, {
      params: { title, description, severity }
    });
    return response.data;
  },
  
  getBugReports: async () => {
    const api = createAuthAxios();
    const response = await api.get('/help/bug-reports');
    return response.data;
  }
};

// Files API
export const filesApi = {
  upload: async (file, formId, elementId) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('form_id', formId);
    formData.append('element_id', elementId);
    
    // IMPORTANT: do NOT manually set `Content-Type: multipart/form-data`
    // here. The browser must set it itself so it includes the
    // auto-generated `boundary=...` substring — without that boundary the
    // server's multipart parser can't frame the body and CDN proxies
    // (Cloudflare etc.) drop the request, producing a "no response"
    // failure that surfaces to the user as a generic network error.
    const response = await axios.post(`${API}/files/upload`, formData);
    return response.data;
  },
  
  uploadVoiceRecording: async (base64Data, formId, elementId) => {
    // Convert base64 to blob
    const byteCharacters = atob(base64Data.split(',')[1] || base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'audio/webm' });
    
    const formData = new FormData();
    formData.append('file', blob, `voice_${Date.now()}.webm`);
    if (formId) formData.append('form_id', formId);
    
    // Same rule applies — let the browser set Content-Type with the
    // multipart boundary itself. Manually setting it strips the
    // boundary and the request is rejected by CDNs in production.
    const response = await axios.post(`${API}/files/upload/voice`, formData);
    return response.data;
  },
  
  get: async (fileId) => {
    const response = await axios.get(`${API}/files/${fileId}`);
    return response.data;
  },
  
  getDownloadUrl: (fileId) => {
    return `${API}/files/${fileId}/download`;
  },
  
  delete: async (fileId) => {
    const api = createAuthAxios();
    const response = await api.delete(`/files/${fileId}`);
    return response.data;
  },
  
  getFormFiles: async (formId) => {
    const response = await axios.get(`${API}/files/form/${formId}`);
    return response.data;
  }
};

// Analytics API
export const analyticsApi = {
  getFormAnalytics: async (formId, excludeIp, period, startDate, endDate) => {
    const api = createAuthAxios();
    const params = {};
    if (excludeIp) params.exclude_ip = excludeIp;
    // Note: period can be 0 (=> "All time"), so we explicitly check for
    // null/undefined instead of truthiness.
    if (period !== undefined && period !== null) params.period = period;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    const response = await api.get(`/analytics/forms/${formId}`, { params });
    return response.data;
  },
  
  getDashboard: async (excludeIp) => {
    const api = createAuthAxios();
    const params = excludeIp ? { exclude_ip: excludeIp } : {};
    const response = await api.get('/analytics/dashboard', { params });
    return response.data;
  },
  
  getMyIp: async () => {
    const api = createAuthAxios();
    const response = await api.get('/analytics/my-ip');
    return response.data;
  },

  resetFormStats: async (formId) => {
    const api = createAuthAxios();
    const response = await api.delete(`/analytics/forms/${formId}/reset`);
    return response.data;
  },
  
  trackView: async (formId) => {
    try {
      await axios.post(`${API}/analytics/track/view`, { form_id: formId });
    } catch (e) {
      // Silent fail for analytics
    }
  },
  
  trackSubmission: async (formId) => {
    try {
      await axios.post(`${API}/analytics/track/submission`, { form_id: formId });
    } catch (e) {
      // Silent fail for analytics
    }
  },

  getFormElementAnalytics: async (formId) => {
    const api = createAuthAxios();
    const response = await api.get(`/analytics/forms/${formId}/elements`);
    return response.data;
  }
};

// Billing API
export const billingApi = {
  createCheckout: async (originUrl) => {
    const api = createAuthAxios();
    // Refersion referral capture — read the `_rfsn` cookie the tracker
    // script (loaded from index.html) writes when a visitor arrives
    // via an affiliate link. Fallback to a `?rfsn=` URL param for
    // browsers that block third-party cookies until interaction.
    let refersionReferral = null;
    try {
      const m = document.cookie.match(/(?:^|;\s*)_rfsn=([^;]+)/);
      if (m) refersionReferral = decodeURIComponent(m[1]);
    } catch { /* silent */ }
    if (!refersionReferral) {
      try {
        const p = new URLSearchParams(window.location.search).get('rfsn');
        if (p) refersionReferral = p;
      } catch { /* silent */ }
    }
    const response = await api.post('/billing/create-checkout', { 
      origin_url: originUrl,
      refersion_referral: refersionReferral
    });
    return response.data;
  },
  
  getCheckoutStatus: async (sessionId) => {
    const api = createAuthAxios();
    const response = await api.post('/billing/checkout-status', { session_id: sessionId });
    return response.data;
  },
  
  getSubscription: async () => {
    const api = createAuthAxios();
    const response = await api.get('/billing/subscription');
    return response.data;
  },
  
  getInvoices: async () => {
    const api = createAuthAxios();
    const response = await api.get('/billing/invoices');
    return response.data;
  },
  
  cancelSubscription: async () => {
    const api = createAuthAxios();
    const response = await api.post('/billing/cancel');
    return response.data;
  }
};

// Auth API (for email verification)
export const authApi = {
  verifyEmail: async (token) => {
    const response = await axios.get(`${API}/auth/verify-email`, {
      params: { token }
    });
    return response.data;
  },
  
  resendVerification: async (email) => {
    const response = await axios.post(`${API}/auth/resend-verification`, null, {
      params: { email }
    });
    return response.data;
  }
};

// Saved Views API — per-user, per-form filter presets on /submissions
export const savedViewsApi = {
  list: async (formId) => {
    const api = createAuthAxios();
    const response = await api.get(`/saved-views/forms/${formId}`);
    return response.data;
  },
  create: async (formId, name, config, isDefault = false) => {
    const api = createAuthAxios();
    const response = await api.post(`/saved-views`, {
      form_id: formId,
      name,
      config,
      is_default: isDefault,
    });
    return response.data;
  },
  update: async (viewId, patch) => {
    const api = createAuthAxios();
    const response = await api.put(`/saved-views/${viewId}`, patch);
    return response.data;
  },
  remove: async (viewId) => {
    const api = createAuthAxios();
    const response = await api.delete(`/saved-views/${viewId}`);
    return response.data;
  },
};

// Backups — per-user form archives downloaded from /settings → Backups.
export const backupsApi = {
  // Lightweight picker manifest used to render the checklist.
  manifest: async () => {
    const api = createAuthAxios();
    const response = await api.get('/backups/manifest');
    return response.data;
  },
  // Build + return the JSON envelope for the selected forms. Pass an
  // empty array (or no IDs) to back up everything.
  export: async ({ formIds = [], includeSubmissions = false } = {}) => {
    const api = createAuthAxios();
    const response = await api.post('/backups/export', {
      form_ids: formIds.length ? formIds : null,
      include_submissions: includeSubmissions,
    });
    return response.data;
  },
  // Preview the contents of a backup envelope without writing anything.
  // Returns per-form metadata + duplicate flags so the user can decide
  // which entries to restore.
  preview: async (envelope) => {
    const api = createAuthAxios();
    const response = await api.post('/backups/preview', { envelope });
    return response.data;
  },
  // Restore selected entries. `duplicateMode`: 'skip' | 'copy' | 'force'.
  restore: async ({ envelope, indices, duplicateMode = 'copy', restoreSubmissions = false }) => {
    const api = createAuthAxios();
    const response = await api.post('/backups/restore', {
      envelope,
      indices,
      duplicate_mode: duplicateMode,
      restore_submissions: restoreSubmissions,
    });
    return response.data;
  },
};


// ── SSO (OIDC) — Pro-only ────────────────────────────────────────────
// The config endpoints are hit from /settings; the /authorize + /callback
// endpoints are hit directly by the browser as full-page navigations.
export const ssoApi = {
  getConfig: async () => {
    const api = createAuthAxios();
    const response = await api.get('/sso/config');
    return response.data;   // null when unconfigured, otherwise the config row
  },
  saveConfig: async (payload) => {
    const api = createAuthAxios();
    const response = await api.put('/sso/config', payload);
    return response.data;
  },
  testIssuer: async (issuer_url) => {
    const api = createAuthAxios();
    const response = await api.post('/sso/test', { issuer_url });
    return response.data;
  },
};

// ── SCIM 2.0 provisioning — token management endpoints ──────────────
// The `/api/scim/v2/Users` endpoints themselves are consumed by the
// customer's IdP, not by our frontend. The frontend only manages the
// token lifecycle here.
export const scimApi = {
  getInfo: async () => {
    const api = createAuthAxios();
    const response = await api.get('/scim/token/info');
    return response.data;
  },
  issue: async () => {
    const api = createAuthAxios();
    const response = await api.post('/scim/token');
    return response.data; // { token: 'scim_...', prefix: 'scim_abc123' } — token shown ONCE
  },
  rotate: async () => {
    const api = createAuthAxios();
    const response = await api.post('/scim/token/rotate');
    return response.data;
  },
  revoke: async () => {
    const api = createAuthAxios();
    const response = await api.delete('/scim/token');
    return response.data;
  },
};

// ── IP Allowlist — Pro-only, per-workspace ─────────────────────────
// Gates dashboard login for a workspace to a CIDR list. See
// backend/routers/ip_allowlist.py + services/ip_allowlist.py.
export const ipAllowlistApi = {
  get: async (workspaceId) => {
    const api = createAuthAxios();
    const response = await api.get(`/workspaces/${workspaceId}/ip-allowlist`);
    return response.data;
  },
  save: async (workspaceId, payload) => {
    const api = createAuthAxios();
    const response = await api.put(`/workspaces/${workspaceId}/ip-allowlist`, payload);
    return response.data;
  },
};

// ── Malware Scanning — Pro-only, per-workspace ─────────────────────
// Sits on `workspaces.settings.malware_scan`. The API returns
// `{enabled}`; the scanning provider is a platform detail managed
// via env secrets, never exposed to the frontend.
export const malwareScanApi = {
  get: async (workspaceId) => {
    const api = createAuthAxios();
    const response = await api.get(`/workspaces/${workspaceId}/malware-scan`);
    return response.data;
  },
  save: async (workspaceId, payload) => {
    const api = createAuthAxios();
    const response = await api.put(`/workspaces/${workspaceId}/malware-scan`, payload);
    return response.data;
  },
  // Scan Health widget: daily counts of rejected + skipped events
  // over the last `days` days. Backend reads audit_logs; owner-only.
  stats: async (workspaceId, days = 7) => {
    const api = createAuthAxios();
    const response = await api.get(`/workspaces/${workspaceId}/malware-scan/stats?days=${days}`);
    return response.data;
  },
};

// ── Teams / Workspaces ──────────────────────────────────────────────
// Minimal helper for pages that only need to know the caller's owned
// workspaces (SSO settings, IP allowlist, etc.).
export const workspacesApi = {
  list: async () => {
    const api = createAuthAxios();
    const response = await api.get('/teams/workspaces');
    return response.data;
  },
};


// ── IndexNow (Bing / Yandex / Naver / Seznam re-crawl trigger) ──────
// Called from the Admin overview to push the entire sitemap to Bing
// after a deploy that changes SEO copy. IndexNow accepts the payload,
// then search engines re-fetch the URLs within hours instead of days.
export const indexnowApi = {
  submitSitemap: async () => {
    const api = createAuthAxios();
    const response = await api.post('/indexnow/submit-sitemap');
    return response.data;
  },
};
